import { useEffect, useRef } from 'react';
import { useWorkspaceLayout } from '../context/WorkspaceLayoutContext';

/**
 * Global workspace keyboard shortcuts (one window-level keydown listener, bubble phase, so
 * components that handle a key themselves - the editor's Mod-J on a selection, Esc in a menu -
 * win by stopping propagation or calling preventDefault).
 *
 * Mod = Cmd on macOS, Ctrl elsewhere.
 */

/** Shortcut catalogue shown in the shortcuts dialog and command palette. */
export const WORKSPACE_SHORTCUTS = [
  { id: 'palette', keys: 'Mod+K', label: 'Search and run commands', group: 'Workspace' },
  { id: 'inky', keys: 'Mod+J', label: 'Show or hide Inky', group: 'Inky' },
  { id: 'dock-side', keys: 'Mod+\\', label: 'Move Inky (right, bottom, left, floating)', group: 'Inky' },
  { id: 'focus', keys: 'Mod+Shift+F', label: 'Turn focus mode on or off', group: 'Workspace' },
  { id: 'exit', keys: 'Esc', label: 'Leave focus mode or close the Inky sheet', group: 'Workspace' },
  { id: 'help', keys: '?', label: 'Show keyboard shortcuts', group: 'Workspace' },
  { id: 'save', keys: 'Mod+S', label: 'Save the open document', group: 'Editor' },
  { id: 'find', keys: 'Mod+F', label: 'Find and replace in the document', group: 'Editor' },
  { id: 'ask', keys: 'Mod+J', label: 'Ask AI about the selected text (with a selection)', group: 'Editor' },
  { id: 'editor-help', keys: 'Mod+/', label: 'All editor shortcuts', group: 'Editor' },
];

/**
 * Whether the user is on macOS / iOS (for Cmd vs Ctrl labels).
 * @returns {boolean} True on Apple platforms.
 */
export function isApplePlatform() {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '';
  return /mac|iphone|ipad|ipod/i.test(platform);
}

/**
 * Human label for a shortcut ('Mod+Shift+F' -> 'Ctrl+Shift+F' or the macOS symbols).
 * @param {string} keys - Shortcut in 'Mod+Shift+X' form.
 * @param {boolean} [apple] - Override platform detection.
 * @returns {string} Label.
 */
export function formatShortcut(keys, apple = isApplePlatform()) {
  const parts = String(keys).split('+').map((part) => (part === '' ? '+' : part));
  if (apple) {
    const symbols = { Mod: '⌘', Shift: '⇧', Alt: '⌥', Ctrl: '⌃', Esc: 'Esc' };
    return parts.map((part) => symbols[part] || part.toUpperCase()).join('');
  }
  return parts.map((part) => (part === 'Mod' ? 'Ctrl' : part.length === 1 ? part.toUpperCase() : part)).join('+');
}

/**
 * Whether an event target is a text-entry field (inputs, textareas, rich text).
 * @param {EventTarget|null} target - Event target.
 * @returns {boolean} True when typing there.
 */
export function isEditableTarget(target) {
  if (!target || typeof target.closest !== 'function') return false;
  if (target.isContentEditable) return true;
  return Boolean(target.closest('input, textarea, select, [contenteditable="true"], .ProseMirror'));
}

/**
 * Whether a modal surface (dialog, menu, popover) is open; those own Esc and focus.
 * @returns {boolean} True when one is open.
 */
export function isModalOpen() {
  if (typeof document === 'undefined') return false;
  return Boolean(document.querySelector('.MuiModal-root:not(.MuiModal-hidden), [role="dialog"][aria-modal="true"]'));
}

/**
 * Parse a keydown into a shortcut id (or null).
 * @param {KeyboardEvent} event - Key event.
 * @returns {'palette'|'inky'|'dock-side'|'focus'|'exit'|'help'|null} Shortcut id.
 */
export function matchShortcut(event) {
  const mod = event.metaKey || event.ctrlKey;
  const key = String(event.key || '');
  const lower = key.toLowerCase();
  if (mod && !event.altKey) {
    if (lower === 'k' && !event.shiftKey) return 'palette';
    if (lower === 'j' && !event.shiftKey) return 'inky';
    if ((key === '\\' || event.code === 'Backslash') && !event.shiftKey) return 'dock-side';
    if (lower === 'f' && event.shiftKey) return 'focus';
    return null;
  }
  if (!mod && !event.altKey && key === 'Escape') return 'exit';
  if (!mod && !event.altKey && key === '?') return 'help';
  return null;
}

/**
 * Install the global shortcuts.
 * @param {{enabled?: boolean}} [options] - Disable outside the app shell.
 * @returns {void}
 */
export default function useWorkspaceShortcuts({ enabled = true } = {}) {
  const layout = useWorkspaceLayout();
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event) => {
      const id = matchShortcut(event);
      if (!id || event.defaultPrevented || event.isComposing) return;
      const l = layoutRef.current;
      const modal = isModalOpen();
      switch (id) {
        case 'palette':
          if (modal) return;
          event.preventDefault();
          l.setCommandPaletteOpen(true);
          break;
        case 'inky':
          if (modal) return;
          event.preventDefault();
          if (l.dock.open) l.toggleDock(false);
          else l.openInky({ focus: true });
          break;
        case 'dock-side':
          if (modal) return;
          event.preventDefault();
          l.cycleDockSide();
          break;
        case 'focus':
          if (modal) return;
          event.preventDefault();
          l.toggleFocusMode();
          break;
        case 'exit':
          // Text fields and the editor handle their own Esc (the editor leaves focus mode itself).
          if (modal || isEditableTarget(event.target)) return;
          if (l.focusMode) {
            event.preventDefault();
            l.setFocusMode(false);
          } else if (l.dock.open && l.dock.variant === 'overlay') {
            event.preventDefault();
            l.toggleDock(false);
          }
          break;
        case 'help':
          if (modal || isEditableTarget(event.target)) return;
          event.preventDefault();
          l.setShortcutsHelpOpen(true);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
